/* eslint-disable no-underscore-dangle */
import { isString } from 'src/researcher/utils';
import { GenericObject } from 'core/models/platform';
import { httpDelete, httpGet, httpPost, httpPut } from 'core/utils/httpClient';
import { CitationData } from 'src/citation-generator/types';
import {
  CreateProjectAPIRequest,
  CreateProjectAPIResponse,
  ListProjectAPIResponse,
} from 'src/researcher/types/project';
import {
  BookmarkServerResponse,
  CitationExportFormat,
  ExportCitationResponse,
  GenerateCitationsResponse,
  GetCitationStylesResponse,
  SaveBookmarkAPIRequest,
  SaveBookmarkSuccessAPIResponse,
  SearchAPIRequest,
  SearchAPIResponse,
  TopicAndStatsAPIResponse,
  TrackSearchRequest,
  TemplateListAPIRequest,
  TemplateListAPIResponse,
} from 'src/researcher/types/api';
import { AutoCompleteRequest, SearchRequest } from 'src/researcher/models/api';

export const getSugestionsFromGPT3 = (data: AutoCompleteRequest) =>
  httpPost('/write-assist/predict-next-text-m1/', data);

export const getSugestionsFromGPTJ = (data: AutoCompleteRequest) =>
  httpPost('/write-assist/predict-next-text-m2/', data);

export interface AutoSuggestResponse {
  predictions: string[];
  run_times: {
    start: number;
    end: number;
  };
  isEngineDowngraded: boolean;
  totalUsageCount: number;
}

interface CitationRequest extends Omit<CitationData, 'id'> {
  _id: string | null;
}

export const getUrls = (data: SearchRequest) => httpPost('/write-assist/search/', data);

export const getGrammerCheck = (data: { text: string }) =>
  httpPost('/write-assist/grammar-check/', data);

export const createProjectAPI = (data: CreateProjectAPIRequest) =>
  httpPost<CreateProjectAPIResponse>('/write-assist/create-project/', data);

export const restoreProjectAPI = (projectId: string) =>
  httpGet(`/write-assist/restore-project/${projectId}/`).then((restoredProject) => {
    const { bookmarks = [] } = restoredProject;
    restoredProject.bookmarks = bookmarks.map((bookmark: any) => {
      const modifiedBookmarkObj = {} as BookmarkServerResponse;
      modifiedBookmarkObj.id = bookmark.id;
      modifiedBookmarkObj.url = bookmark.link;
      modifiedBookmarkObj.title = bookmark.name;
      modifiedBookmarkObj.genre = bookmark.mediaType;
      modifiedBookmarkObj.preview = bookmark.previewText;
      return modifiedBookmarkObj;
    });
    return restoredProject;
  });

export const saveCheckpoint = (
  data: {
    title?: string;
    html?: string | null;
    autosaved?: boolean;
    citations?: CitationData[] | null;
    preview?: string;
    citationStyleId?: string;
  },
  projectId: string
) => {
  const payload = { ...data };
  if (data.citations) {
    const modifiedCitations = data.citations.map((citation: CitationData) => {
      const modifiedCitationObj: any = { ...citation, _id: citation.id };
      delete modifiedCitationObj.id;
      delete modifiedCitationObj.resourceId;
      delete modifiedCitationObj._position_;
      delete modifiedCitationObj._status_;
      return modifiedCitationObj;
    });
    payload.citations = modifiedCitations;
  }

  return httpPost<{ totalStorageUsed: number }>(
    `/write-assist/save-checkpoint/${projectId}`,
    payload
  );
};

export const saveScratchpad = (html: string, projectId: string) =>
  httpPut(`/write-assist/save-scratchpad/${projectId}`, {
    html,
  });

export const deleteProjectAPI = (projectId: string) =>
  httpDelete(`/write-assist/delete-project/${projectId}`, {});

export const undeleteProjectAPI = (projectId: string) =>
  httpPost(`/write-assist/undelete-project/${projectId}`, {});

export const listProjectsAPI = ({
  skip,
  limit,
  query,
  status,
  sortBy,
  orderBy,
}: {
  skip?: number;
  limit?: number;
  query?: string;
  status?: 'live' | 'trash';
  sortBy?: string;
  orderBy?: string;
}) => {
  const data: GenericObject = {
    skip: skip || 0,
    limit: limit || 10,
  };

  if (isString(query)) {
    data.query = query;
  }

  if (isString(status)) {
    data.status = status;
  }

  if (isString(sortBy)) {
    data.sortBy = sortBy;
  }

  if (isString(orderBy)) {
    data.orderBy = orderBy;
  }

  return httpGet<ListProjectAPIResponse>('/write-assist/list-projects/', data);
};

export const listProjectCitations = (projectId: string) =>
  httpGet(`/write-assist/projects/${projectId}/citations`);

export const searchAPI = (request: SearchAPIRequest): Promise<SearchAPIResponse> =>
  httpPost<SearchAPIResponse>('/write-assist/search/', request);

export const deleteBookmarkAPI = (projectId: string, bookmarkId: string) =>
  httpDelete<{ totalStorageUsed: number }>(
    `/write-assist/delete-bookmark/${projectId}/${bookmarkId}`,
    {}
  );

export const saveBookmarkAPI = (projectId: string, request: SaveBookmarkAPIRequest) => {
  const { url: link, title: name, preview: previewText, genre: mediaType } = request;
  return httpPut<SaveBookmarkSuccessAPIResponse>(`/write-assist/save-bookmark/${projectId}`, {
    link,
    name,
    previewText,
    mediaType,
  }).then((response) => {
    const { id, totalStorageUsed } = response;
    return {
      id,
      totalStorageUsed,
      ...request,
    };
  });
};

export const getTopicAndContentAPI = (url: string, skipStats = false) =>
  // eslint-disable-next-line no-async-promise-executor
  new Promise<TopicAndStatsAPIResponse>(async (resolve, reject) => {
    const timer = setTimeout(() => {
      reject();
    }, 15000);
    try {
      const response = await httpPost<TopicAndStatsAPIResponse>('/write-assist/get-url-data/', {
        url,
        skipStats,
      });
      resolve(response);
      clearTimeout(timer);
    } catch (err) {
      clearTimeout(timer);
      reject(err);
    }
  });

export const trackSearchAPI = (data: TrackSearchRequest) =>
  httpPost('/write-assist/track-search/', data);

/**
 * CITATION GENERATOR ENDPOINTS
 */

export const getCitationStyles = ({
  page,
  count,
  search,
  currentStyle,
}: {
  page?: number;
  count?: number;
  search?: string;
  currentStyle?: string;
}) => httpGet<GetCitationStylesResponse>('/write-assist/citation-styles');

export const generateCitations = ({
  style,
  citations,
}: {
  style: string;
  citations: CitationData[];
}) =>
  httpPost<GenerateCitationsResponse>('/write-assist/generate-citations', {
    style: style.toLowerCase(),
    citations,
  });

export const exportCitations = ({
  format,
  style,
  citations,
}: {
  format: CitationExportFormat;
  style: string;
  citations: CitationData[];
}) =>
  httpPost<ExportCitationResponse>('/write-assist/export-citations', { format, style, citations });

export const listTemplatesAPI = ({ skip, limit }: TemplateListAPIRequest) =>
  httpGet<TemplateListAPIResponse>('/write-assist/list-templates/', {
    skip,
    limit,
  });
